import { useEffect, useMemo, useRef, useState } from 'react';

import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { KimInput } from './kim-input';
import { OrgPermission } from '../../../../../../../../../../backend/src/organisation/organisation-permission';
import { useKimFileUpload } from '../hooks/use-kim-file-upload';
import { TEMPLATES } from '../templates';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateKimThread } from '@/api/mutations/useCreateKimThread';
import { useGetPromptTemplates } from '@/api/queries/useGetPromptTemplates';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { BetaBadge } from '@/new/components/beta-badge';
import { DocMentionsInputHandle } from '@/new/components/doc-mentions-input';
import { Icon } from '@/new/components/icon';
import { NewChip } from '@/new/components/new-chip';
import { Button } from '@/new/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/new/components/shadcn/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';

export function NewKimLargeInput() {
  const { matterId, subsetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [question, setQuestion] = useState('');
  const [pendingAutoSubmit, setPendingAutoSubmit] = useState(false);
  const inputRef = useRef<DocMentionsInputHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const questionFromSearch = next.get('question')?.trim();
    const shouldAutoSubmit = next.get('autoSubmit') === 'true';

    if (questionFromSearch) {
      next.delete('question');
      next.delete('autoSubmit');
      setSearchParams(next, { replace: true });
      setQuestion(questionFromSearch);
      if (shouldAutoSubmit) setPendingAutoSubmit(true);
      return;
    }

    const templateId = next.get('template');
    if (templateId) {
      const template = TEMPLATES.find((t) => t.id === templateId);
      if (template) {
        setQuestion(template.template);
      }

      next.delete('template');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.has('question') || searchParams.has('template')) return;

    const questionFromState = (location.state as { question?: string } | null)?.question?.trim();
    if (questionFromState) {
      setQuestion(questionFromState);
    }
  }, []);

  const { mutate: createThread, isLoading } = useCreateKimThread(matterId, subsetId);
  const { uploadedFiles, isUploading, addFiles, removeFile, clearFiles } = useKimFileUpload(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);
  const { isEnabled } = useOrgPermissions();
  const isTemplateBuilderEnabled = isEnabled(OrgPermission.KimTemplateBuilder);
  const { data: userTemplates = [] } = useGetPromptTemplates({ enabled: isTemplateBuilderEnabled });

  const suggestedQuestions = useMemo(() => {
    const questions = subset?.exampleKimQuestions?.question?.map((q) => q.prompt) ?? [];
    const drafts = subset?.exampleKimQuestions?.draft?.map((q) => q.prompt) ?? [];
    return [...questions, ...drafts];
  }, [subset]);

  const handleSubmit = () => {
    const trimmed = question.trim();
    if (!trimmed || isUploading) return;

    const files = uploadedFiles.length > 0 ? uploadedFiles : undefined;

    createThread(
      { message: trimmed, files },
      {
        onSuccess: (data) => {
          clearFiles();
          navigate(`/matters/${matterId}/subsets/${subsetId}/new-kim/${data.id}`);
        },
        onError: () => {
          toast.error('Error creating thread');
        },
      },
    );
  };

  useEffect(() => {
    if (!pendingAutoSubmit) return;
    if (!question.trim() || isUploading || isLoading) return;
    setPendingAutoSubmit(false);
    handleSubmit();
  }, [pendingAutoSubmit, question, isUploading, isLoading]);

  return (
    <div className="mb-12 flex w-full max-w-xl flex-col items-center">
      <Tooltip>
        <TooltipTrigger>
          <BetaBadge className="mb-3" />
        </TooltipTrigger>
        <TooltipContent sideOffset={14}>
          This version of Kim is still undergoing testing. Please send any feedback to{' '}
          <a href="mailto:support@wexler.ai" className="text-blue-500">
            support@wexler.ai
          </a>
          .
        </TooltipContent>
      </Tooltip>

      <h2 className="mb-8 font-serif text-2xl font-medium">Ask Kim...</h2>

      <div className="mb-1.5 flex w-full justify-between">
        <div className="flex">
          <Button variant={'ghost'} size={'sm'} onClick={() => inputRef.current?.triggerMention()}>
            <Icon className="lucide--at-sign" /> Mention
          </Button>
          {isEnabled(OrgPermission.KimFileUpload) && (
            <Button
              variant="ghost"
              size={'sm'}
              disabled={isLoading || isUploading}
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon className="lucide--paperclip" />
              Attach files
            </Button>
          )}
        </div>

        {isEnabled(OrgPermission.KimFileUpload) && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length > 0) addFiles?.(files);
              e.target.value = '';
            }}
          />
        )}

        <div className="flex">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant={'ghost'} size={'sm'} className="pr-0">
                Suggested Questions
                <Icon className="lucide--chevron-down" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="group/suggestions -mt-1.5 max-h-90 max-w-md space-y-1.5 bg-gray-100 p-1.5"
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              {suggestedQuestions.length === 0 ? (
                <DropdownMenuItem disabled className="text-xs">
                  No suggested questions
                </DropdownMenuItem>
              ) : (
                <>
                  <p className="px-1.5 py-px text-xs text-gray-400">
                    <strong>{suggestedQuestions.length}</strong> suggested question
                    {suggestedQuestions.length === 1 ? '' : 's'}
                  </p>
                  {suggestedQuestions.map((prompt, index) => (
                    <DropdownMenuItem
                      key={index}
                      className="bg-white px-2.25 py-1.5 whitespace-normal text-gray-700 hover:bg-white! group-has-[:hover]/suggestions:[&:not(:hover)]:text-gray-600 group-has-[:hover]/suggestions:[&:not(:hover)]:opacity-80"
                      onClick={() => {
                        setQuestion(prompt);
                        inputRef.current?.focus();
                      }}
                    >
                      {prompt}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant={'ghost'} size={'sm'}>
                {isTemplateBuilderEnabled && <NewChip className="relative top-auto right-auto" />}
                Templates
                <Icon className="lucide--chevron-down" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="-mt-1.5 mr-1" onCloseAutoFocus={(e) => e.preventDefault()}>
              {TEMPLATES.map((template) => (
                <DropdownMenuItem
                  key={template.id}
                  onClick={() => {
                    setQuestion(template.template);
                    inputRef.current?.focus();
                  }}
                  className="text-[13px]"
                >
                  <Icon className={template.icon} /> {template.label}
                </DropdownMenuItem>
              ))}

              {isTemplateBuilderEnabled && userTemplates.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  {userTemplates.map((template) => (
                    <DropdownMenuItem
                      key={template.id}
                      onClick={() => {
                        setQuestion(template.body);
                        inputRef.current?.focus();
                      }}
                      className="text-[13px]"
                    >
                      {template.title}
                    </DropdownMenuItem>
                  ))}
                </>
              )}

              {isTemplateBuilderEnabled && (
                <>
                  <DropdownMenuSeparator />

                  <DropdownMenuItem
                    key="new-template"
                    onClick={() => navigate(`/matters/${matterId}/subsets/${subsetId}/new-kim/template-builder`)}
                    className="text-[13px]"
                  >
                    <Icon className="lucide--plus" /> Build a template
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <KimInput
        ref={inputRef}
        className="min-h-28 w-full p-4 py-3 pb-0"
        placeholder="What is this Matter about?"
        value={question}
        onChange={setQuestion}
        onSubmit={handleSubmit}
        disabled={isLoading}
        uploadedFiles={uploadedFiles}
        isUploading={isUploading}
        onFilesAdded={addFiles}
        onFileRemove={removeFile}
        autoFocus
      />

      <Link
        to={`/matters/${matterId}/subsets/${subsetId}/kim`}
        className="mt-5 flex items-center justify-center gap-1.5 text-center text-xs text-gray-400 duration-150 hover:text-gray-600"
        onClick={() => {
          trackCustomEvent(TrackingEventsV2.KimReturnToOldExperience, { matterId, subsetId });
          localStorage.setItem('isUsingNewKim', 'false');
        }}
      >
        <Icon className="lucide--undo-2 mb-0.5 -ml-1" /> Return to the old Kim experience
      </Link>
    </div>
  );
}
